import { useEffect, useState } from "react";
import { Play, Square, Check, Search } from "lucide-react";
import { API } from "@/lib/api";
import { VOICE_SAMPLE } from "@/lib/voice";
import { playUrl, stopAudio, ttsUrl } from "@/lib/audio";

const LEGACY = {
  shimmer: "EXAVITQu4vr4xnSDxMaL",
  coral: "cgSgspJ2msm6clMCkdW9",
  nova: "FGY2WhTYpPnrIDTdsKH5",
  onyx: "nPczCjzI2devNBz1zQrb",
};

const genderLabel = (g) => (g === "male" ? "Masculine" : g === "female" ? "Féminine" : g || "");

export function VoicePicker({ value, onChange }) {
  const [voices, setVoices] = useState([]);
  const [q, setQ] = useState("");
  const [playing, setPlaying] = useState(null);
  const [loading, setLoading] = useState(true);

  const stop = () => {
    stopAudio();
    setPlaying(null);
  };

  useEffect(() => {
    fetch(`${API}/tts/voices`).then((r) => r.json()).then((d) => setVoices(d.voices || []))
      .catch(() => {}).finally(() => setLoading(false));
    return () => stop();
  }, []);

  const preview = async (id) => {
    if (playing === id) { stop(); return; }
    stop();
    setPlaying(id);
    try {
      await playUrl(ttsUrl(VOICE_SAMPLE, id));
    } catch (e) {}
    setPlaying((p) => (p === id ? null : p));
  };

  const effective = LEGACY[value] || value;
  const filtered = voices.filter((v) => {
    const s = `${v.name} ${genderLabel(v.gender)} ${v.accent || ""} ${v.description || ""} ${v.use_case || ""}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  return (
    <div className="space-y-2" data-testid="voice-picker">
      {voices.length > 6 && (
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="voice-search" value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher une voix (genre, accent…)"
            className="w-full h-9 rounded-full bg-black/30 border border-white/10 pl-9 pr-3 text-sm outline-none focus:border-primary/40" />
        </div>
      )}
      <div className="space-y-2 max-h-[320px] overflow-y-auto pr-1">
        {loading && <p className="text-sm text-muted-foreground py-4 text-center">Chargement des voix…</p>}
        {!loading && filtered.map((v) => {
          const active = effective === v.id;
          return (
            <div key={v.id} data-testid={`voice-row-${v.id}`} onClick={() => onChange(v.id)}
              className={`flex items-center gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-colors ${
                active ? "border-primary/60 bg-primary/10" : "border-white/10 hover:border-white/25"}`}>
              <button data-testid={`voice-preview-${v.id}`} aria-label={`Écouter ${v.name}`}
                onClick={(e) => { e.stopPropagation(); preview(v.id); }}
                className="w-9 h-9 rounded-full flex items-center justify-center shrink-0 bg-primary/15 text-primary hover:bg-primary/25 transition-colors">
                {playing === v.id ? <Square size={14} /> : <Play size={14} className="ml-0.5" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{v.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {[genderLabel(v.gender), v.accent, v.description || v.use_case].filter(Boolean).join(" · ") || "Voix ElevenLabs"}
                </p>
              </div>
              {active && <Check size={16} className="text-primary shrink-0" />}
            </div>
          );
        })}
        {!loading && filtered.length === 0 && (
          <p className="text-sm text-muted-foreground py-4 text-center">Aucune voix trouvée.</p>
        )}
      </div>
    </div>
  );
}
